import { LocationMarkerIcon } from "@heroicons/react/outline";
import Color from "color";
import { head, isEmpty } from "lodash";
import { Map } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import React, { useCallback, useMemo, useState } from "react";
import ReactMapboxGl, { MapContext } from "react-mapbox-gl";
import { matchPath, useLocation } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import { useAppearance } from "../../../context/appearance";
import { useLineOfSight } from "../../../context/lineOfSight";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useLocations } from "../../../context/locations";
import classNames from "../../../utils/classNames";
import useMapCenter, { Coordinates } from "../../../utils/useMapCenter";
import useMapConfig, { VisualizationLayer } from "../../../utils/useMapConfig";
import MapContent from "./MapContent";

const Mapbox = ReactMapboxGl({
  accessToken: process.env.REACT_APP_MAPBOX_KEY ?? "",
  interactive: true,
  minZoom: 2,
  logoPosition: "top-left"
});

interface Props {
  containerStyle?: React.CSSProperties;
}

const MapComponent: React.FC<Props> = ({ containerStyle = {} }) => {
  const { getIsReady } = useLoading();
  const center = useMapCenter((state) => state.center);
  const setCenter = useMapCenter((state) => state.setCenter);
  const setBoundaries = useMapCenter((state) => state.setBoundaries);
  const zoom = useMapCenter((state) => state.zoom);
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const { isDark } = useAppearance();
  const location = useLocation();
  const explorerPageMatch = matchPath<{
    tab?: string; // hotspots, locations, hexes
  }>(location.pathname, {
    path: "/:tab"
  });
  const visibleLayers = useMapConfig((state) => state.visibleLayers);
  const [isMoving, setIsMoving] = useState(false);
  const { renderSelectPointOnMapConfirmationBar, selectPointsOnMapConfig } =
    useLocations();
  const { isMinimized, renderMinimizedComponent } = useLineOfSight();

  const isLoading = useMemo(
    () => !getIsReady([LoadingKey.hotspotsAroundLocations]),
    [getIsReady]
  );

  // TODO: make a component for this
  const generateLegend = useCallback(
    (colorItems: ColorLegendItem[]) => {
      const legendContainer = document.querySelector(
        ".elevation-profile-legend"
      );

      if (!legendContainer) return;

      legendContainer.innerHTML = "";
      for (var i = 0; i < colorItems.length; i++) {
        const genDiv = document.createElement("div");
        genDiv.className = "ele-item";
        const color = Color(colorItems[i].colorhex).alpha(0.5);
        genDiv.style.backgroundColor = color.hsl().string();
        const genSpan = document.createElement("span");
        if (!genSpan) return;

        genSpan.innerHTML = renderDistance(colorItems[i].value, false);
        genDiv.appendChild(genSpan);
        legendContainer.appendChild(genDiv);
      }
    },
    [renderDistance]
  );

  const updateElevationContourColors = useCallback(
    (map: Map) => {
      if (
        !(
          explorerPageMatch?.params.tab &&
          ["hotspots", "locations"].includes(explorerPageMatch?.params.tab) &&
          visibleLayers[VisualizationLayer.elevationContours]
        ) ||
        !map.isSourceLoaded("mapbox-terrain")
      ) {
        return;
      }

      const contourLayers = map.queryRenderedFeatures({
        layers: ["contour"]
      } as any);

      const elevationValues: number[] = [];

      for (var i = 0; i < contourLayers.length; i++) {
        const elevationValue = contourLayers[i].properties?.ele;
        if (!elevationValues.includes(elevationValue) && elevationValue > 0) {
          elevationValues.push(elevationValue);
        }
      }

      if (isEmpty(elevationValues)) return;

      const maxValue = Math.max(...elevationValues);
      const minValue = 0;

      const colors = [
        "transparent",
        "#73c9ca",
        "#85e173",
        "#73f374",
        "#f3f374",
        "#f3d273",
        "#f3b373",
        "#f39373",
        "#f27373"
      ];

      const legendCategories: ColorLegendItem[] = [];

      var colorExpression = ["case"];
      var step = (maxValue - minValue) / (colors.length - 1);

      for (var j = 0; j < colors.length; j++) {
        let elevStep = Math.ceil(minValue + j * step);
        if (j > 0) {
          legendCategories.push({ value: elevStep, colorhex: colors[j] });
        }
        colorExpression.push(["<=", ["get", "ele"], elevStep] as any);
        colorExpression.push(colors[j]);
      }

      generateLegend(legendCategories.reverse());

      colorExpression.push("transparent");

      map.setPaintProperty("filled-contours", "fill-color", colorExpression);
      map.setPaintProperty("contour", "line-color", colorExpression);
    },
    [explorerPageMatch?.params.tab, generateLegend, visibleLayers]
  );

  const onMoveAndResize = useCallback(
    (map: Map, event: React.SyntheticEvent<any>) => {
      const { lng, lat } = map.getCenter();
      const newCenter: Coordinates = [lng, lat];

      if (map.getZoom() < MIN_ZOOM) return;

      try {
        const [neLng, neLat] = map.getBounds().getNorthEast().toArray();
        const [swLng, swLat] = map.getBounds().getSouthWest().toArray();

        setCenter(newCenter);
        setBoundaries({
          ne: [neLng, neLat],
          sw: [swLng, swLat]
        });

        const mapIdle = () => {
          updateElevationContourColors(map);

          if (map.areTilesLoaded()) {
            map.off("moveend", mapIdle);
            map.on("moveend", mapIdle);
          }
        };

        mapIdle();
      } catch {}
    },
    [setCenter, setBoundaries, updateElevationContourColors]
  );

  const getMapboxStyleUrl = useMemo(() => {
    let key = isDark
      ? "ckshalgloh40l17q6aapw2lp9"
      : "ckshap8do7p1617rzndourdz2";

    if (visibleLayers[VisualizationLayer.satellite]) {
      key = isDark ? "ckvb63ua515yi14qig2qsyfbu" : "ckt5sbvz52byi17qogc71dm3p";
    }

    return `mapbox://styles/hotspotty/${key}`;
  }, [isDark, visibleLayers]);

  if (!head(center)) {
    return <div style={{ ...mapboxContainerStyle, ...containerStyle }} />;
  }

  return (
    <Mapbox
      style={getMapboxStyleUrl}
      containerStyle={{ ...mapboxContainerStyle, ...containerStyle }}
      center={center}
      zoom={zoom}
      onMoveEnd={onMoveAndResize}
      onResize={onMoveAndResize}
      onDragStart={() => setIsMoving(true)}
      onDragEnd={() => setIsMoving(false)}
    >
      <MapContext.Consumer>
        {(map) =>
          map ? (
            <div>
              {isMinimized && <>{renderMinimizedComponent}</>}
              {!!selectPointsOnMapConfig && (
                <>
                  {renderSelectPointOnMapConfirmationBar}

                  <div
                    className={classNames(
                      "absolute top-1/2 left-1/2 text-hotspotty-700 dark:text-white",
                      isMoving && "-mt-1"
                    )}
                  >
                    <LocationMarkerIcon
                      style={{ height: "24px", margin: "-17px -12px" }}
                    />
                  </div>
                </>
              )}
              {isLoading && (
                <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity">
                  <CenteredLoading className="h-full" />
                </div>
              )}
              <MapContent
                map={map}
                containerStyle={containerStyle}
                updateElevationContourColors={updateElevationContourColors}
                isMoving={isMoving}
              />
            </div>
          ) : (
            <CenteredLoading className="h-full" />
          )
        }
      </MapContext.Consumer>
    </Mapbox>
  );
};

export default MapComponent;

//
// Utils
//

const mapboxContainerStyle: React.CSSProperties = {
  position: "relative",
  width: "100%",
  overflow: "hidden",
  height: "100%"
};

interface ColorLegendItem {
  value: number;
  colorhex: string;
}

export const MIN_ZOOM = 6;
